/**
 * Collects a Kuali tenant URL and masked workspace credential choice safely.
 *
 * Administrators may overwrite a stored service key but can never retrieve it.
 * The warning keeps personal university passwords and keys out of the shared
 * store while allowing purpose-issued system credentials.
 */
import { Shield } from 'lucide-react'

import {
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@/components/ui'
import type { ConnectorCredentialSource } from '../../types'
import type { KualiResearchConnectionStatus } from '../types'
import { cn } from '@/lib/utils'

interface KualiResearchCredentialsProps {
  source: ConnectorCredentialSource
  onSourceChange: (source: ConnectorCredentialSource) => void
  connection: KualiResearchConnectionStatus
  apiKey: string
  onApiKeyChange: (value: string) => void
  canSelectSource: boolean
  canManageCredential: boolean
}

export function KualiResearchCredentials({
  source,
  onSourceChange,
  connection,
  apiKey,
  onApiKeyChange,
  canSelectSource,
  canManageCredential,
}: KualiResearchCredentialsProps) {
  const workspaceStatus = connection.workspaceConfigured
    ? 'System API key, stored securely'
    : 'Enter a system API key'
  const umdStatus = connection.umdConfigured
    ? 'Managed credential available'
    : 'Not configured'
  const sources = connection.workspaceConfigured === null
    ? ([['umd', 'UMD Credential Store', umdStatus]] as const)
    : ([
        ['workspace', 'Workspace credential', workspaceStatus],
        ['umd', 'UMD Credential Store', umdStatus],
      ] as const)

  return (
    <div className="space-y-3">
      <RadioGroup
        value={source}
        disabled={!canSelectSource}
        onValueChange={value => onSourceChange(value as ConnectorCredentialSource)}
        className={`grid gap-2.5 ${sources.length > 1 ? 'sm:grid-cols-2' : ''}`}
      >
        {sources.map(([value, title, status]) => (
          <label
            key={value}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-[9px] border px-3 py-3 transition-colors',
              source === value
                ? 'border-[1.5px] border-slate-900 bg-white'
                : 'border-slate-200 hover:bg-slate-50',
            )}
          >
            <RadioGroupItem value={value} />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900">
                {title}
              </span>
              <span className="block truncate text-xs text-slate-500">{status}</span>
            </span>
          </label>
        ))}
      </RadioGroup>

      {source === 'workspace'
        ? (
            <div className="space-y-1.5">
              <Label htmlFor="kuali-api-key" className="text-xs font-medium text-slate-700">
                {connection.workspaceConfigured ? (
                  <>Replace API key <span className="font-normal text-slate-400">(optional)</span></>
                ) : 'Kuali Research system API key'}
              </Label>
              <Input
                id="kuali-api-key"
                type="password"
                autoComplete="off"
                value={apiKey}
                disabled={!canManageCredential}
                onChange={event => onApiKeyChange(event.target.value)}
                placeholder={connection.workspaceConfigured ? '•••••••• — leave blank to keep the stored key' : 'Paste the system API key'}
                className="h-9 text-[13px]"
              />
            </div>
          )
        : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
              Ask the credential-store administrator to configure:
              <div className="mt-1 space-y-0.5 break-all font-mono text-slate-700">
                {connection.umdCredentialKeys.map(key => <div key={key}>{key}</div>)}
              </div>
            </div>
          )}

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5 text-xs leading-4 text-amber-900">
        <Shield className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Use the system API key issued for this integration — never a personal UMD password, key, or token.
        </span>
      </div>
    </div>
  )
}
